import { isNativePlatform } from "@/utils/native";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState, type ReactNode } from "react";
import { useLocation, type Location } from "react-router";

const SLIDE_DISTANCE_PX = 28;

/**
 * Sections where every descendant path stays on the same screen — an in-page
 * tab, or a modal rendered over the list. Only use a prefix when that is true
 * of the whole subtree; integrations is listed per section because its root is
 * a hub the sections drill down from.
 */
const TAB_SECTION_PREFIXES = [
  "/dashboard/stripe",
  "/dashboard/notifications",
  "/dashboard/admin",
  "/dashboard/integrations/api",
  "/dashboard/integrations/webhooks",
];

/**
 * Tabs whose siblings are separate screens, so they can't be matched by prefix
 * — /dashboard/products/stock is its own sidebar destination, and
 * /dashboard/services/:id is a drill-down that should still animate.
 */
const TAB_PATHS: Record<string, string> = {
  "/dashboard/products/categories": "/dashboard/products",
  "/dashboard/services/categories": "/dashboard/services",
  "/dashboard/services/addons": "/dashboard/services",
  "/dashboard/services/addons/categories": "/dashboard/services",
  "/dashboard/services/discounts": "/dashboard/services",
};

/**
 * Collapses in-page tab routes onto the screen that owns them, so switching
 * tabs doesn't read as a screen change. Anything else animates on pathname.
 */
function getScreenKey(pathname: string) {
  const normalized = pathname.replace(/\/+$/, "") || "/";

  const section = TAB_SECTION_PREFIXES.find(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );

  return section ?? TAB_PATHS[normalized] ?? normalized;
}

type RouteTransitionProps = {
  /**
   * Receives the location the transition captured — pass it straight to
   * `<Routes location={...}>` so the outgoing screen keeps rendering the route
   * it was on. Without it the exiting copy re-reads the router context and
   * both halves animate the same (new) page.
   */
  children: (location: Location) => ReactNode;
};

export function RouteTransition({ children }: RouteTransitionProps) {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  // react-router keeps a monotonic index on history state; comparing it against
  // the previous render tells a push from a back navigation, which is what
  // makes the direction read as native instead of arbitrary. Derived as state
  // rather than a ref because the direction is needed during this render.
  const [previous, setPrevious] = useState({
    index: (window.history.state?.idx as number | undefined) ?? 0,
    direction: 1,
  });

  const historyIndex = (window.history.state?.idx as number | undefined) ?? 0;

  const direction =
    previous.index === historyIndex
      ? previous.direction
      : historyIndex < previous.index
        ? -1
        : 1;

  if (previous.index !== historyIndex) {
    setPrevious({ index: historyIndex, direction });
  }

  if (!isNativePlatform() || prefersReducedMotion) {
    return <>{children(location)}</>;
  }

  return (
    <AnimatePresence initial={false} mode="popLayout">
      <motion.div
        // Screen, not pathname: the dashboard writes filters and pagination
        // into the query string, and in-page tabs into the path — neither is a
        // screen change worth animating.
        key={getScreenKey(location.pathname)}
        className="w-full"
        initial={{ opacity: 0, x: direction * SLIDE_DISTANCE_PX }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: direction * -SLIDE_DISTANCE_PX }}
        transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
      >
        {children(location)}
      </motion.div>
    </AnimatePresence>
  );
}
